import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Upload, X, Image as ImgIcon, Palette, Globe, Mail, FileText } from "lucide-react";

// Focused whitelabel page: everything a reseller needs to re-skin the storefront
// for their own subdomain — site name, brand colors, logo, footer credit,
// support email. Colors preview live before saving (via SettingsProvider) so
// the admin sees the entire admin+B2C UI recolor in real time.

const COLOR_FIELDS = [
  { k: "primary_color",   l: "Primary",     help: "CTAs, links, main brand fills" },
  { k: "secondary_color", l: "Secondary",   help: "Accent gradients, highlight pills" },
  { k: "accent_color",    l: "Accent (dark)", help: "Headings, titles, dark text" },
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export default function AdminBranding() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { reload: reloadSettings, setSettings: setLiveSettings } = useSettings();

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/settings"); setS(data); }
    catch (_e) { toast.error("Failed to load branding"); }
  })(); }, []);

  if (!s) return <div className="text-slate-500 p-6">Loading branding…</div>;

  // Live-preview colors across the entire admin+B2C UI (before save).
  const updateField = (k, v) => {
    setS((cur) => ({ ...cur, [k]: v }));
    if (k === "primary_color" || k === "secondary_color" || k === "accent_color") {
      setLiveSettings((cur) => ({ ...cur, [k]: v }));
    }
  };

  const invalidHex = COLOR_FIELDS.filter((f) => s[f.k] && !HEX_RE.test(s[f.k])).map((f) => f.k);

  const save = async () => {
    if (invalidHex.length) {
      toast.error(`Fix the color: ${invalidHex.map((k) => k.replace("_color", "")).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      await api.patch("/admin/settings", {
        brand_name: s.brand_name || "",
        primary_color: s.primary_color,
        secondary_color: s.secondary_color,
        accent_color: s.accent_color,
        footer_powered_by: s.footer_powered_by || "",
        contact_email: s.contact_email || "",
      });
      await reloadSettings();
      toast.success("Branding saved — theme applied everywhere");
    } catch (_e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be < 15 MB"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/settings/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setS((cur) => ({ ...cur, logo_url: data.logo_url }));
      await reloadSettings();
      if (data.compression_ratio && data.compression_ratio > 1.2) {
        toast.success(`Logo optimised — ${(data.original_size_bytes / 1024).toFixed(0)} KB → ${(data.final_size_bytes / 1024).toFixed(0)} KB (${data.compression_ratio}× smaller)`);
      } else {
        toast.success("Logo uploaded & optimised");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearLogo = async () => {
    await api.patch("/admin/settings", { logo_url: "" });
    setS((cur) => ({ ...cur, logo_url: "" }));
    await reloadSettings();
    toast.success("Logo cleared — reverted to default");
  };

  const logoSrc = (() => {
    const raw = s.logo_url || "";
    if (!raw) return "";
    if (raw.startsWith("http")) return raw;
    const path = raw.startsWith("/uploads/") ? `/api${raw}` : raw;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  })();

  return (
    <div data-testid="admin-branding-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary)] text-white flex items-center justify-center">
          <Palette className="w-4 h-4" />
        </div>
        <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Whitelabel branding</h1>
      </div>
      <p className="text-slate-500 ml-12">Rebrand the storefront for your own subdomain — site name, colors, logo, footer credit and support email.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        {/* ──────────────── LEFT: editor ──────────────── */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-[var(--brand-accent)] font-semibold text-sm uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5" /> Identity
            </div>
            <div>
              <Label htmlFor="brand_name">Site name</Label>
              <Input id="brand_name" data-testid="branding-brand-name"
                     value={s.brand_name || ""} onChange={(e) => updateField("brand_name", e.target.value)}
                     placeholder="Your travel brand" />
              <div className="text-[11px] text-slate-400 mt-1">Shown in the header, browser tab and transactional emails.</div>
            </div>
            <div>
              <Label htmlFor="contact_email" className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Support email</Label>
              <Input id="contact_email" data-testid="branding-support-email" type="email"
                     value={s.contact_email || ""} onChange={(e) => updateField("contact_email", e.target.value)}
                     placeholder="support@your-brand.com" />
              <div className="text-[11px] text-slate-400 mt-1">Used on the Contact page and every booking-confirmation email.</div>
            </div>
            <div>
              <Label htmlFor="footer_powered_by" className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Footer credit</Label>
              <Input id="footer_powered_by" data-testid="branding-footer"
                     value={s.footer_powered_by || ""} onChange={(e) => updateField("footer_powered_by", e.target.value)}
                     placeholder="Powered by Your Brand LLC" />
              <div className="text-[11px] text-slate-400 mt-1">Small print at the very bottom of every public page.</div>
            </div>
          </Card>

          {/* Logo */}
          <Card className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-[var(--brand-accent)] font-semibold text-sm uppercase tracking-wider">
              <ImgIcon className="w-3.5 h-3.5" /> Brand logo
            </div>
            <div className="flex items-center gap-5 flex-wrap">
              <div className="w-28 h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {logoSrc
                  ? <img src={logoSrc} alt="Logo preview" data-testid="branding-logo-preview" className="max-w-full max-h-full object-contain p-2" />
                  : <ImgIcon className="w-8 h-8 text-slate-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                    onChange={onLogoChange}
                    disabled={uploading}
                    data-testid="branding-logo-input"
                    className="hidden"
                  />
                  <Button as="span" disabled={uploading} type="button" data-testid="branding-logo-upload"
                          className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"
                          onClick={(e) => e.currentTarget.previousSibling.click()}>
                    <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload logo"}
                  </Button>
                </label>
                {s.logo_url && (
                  <Button type="button" variant="outline" size="sm" onClick={clearLogo}
                          data-testid="branding-logo-clear" className="rounded-full">
                    <X className="w-3.5 h-3.5 mr-1" /> Reset to default
                  </Button>
                )}
                <div className="text-[11px] text-slate-400 max-w-xs">
                  PNG / JPG / SVG / WEBP / GIF up to 15 MB — we auto-resize &amp; compress for the header.
                </div>
              </div>
            </div>
          </Card>

          {/* Colors */}
          <Card className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-[var(--brand-accent)] font-semibold text-sm uppercase tracking-wider">
              <Palette className="w-3.5 h-3.5" /> Colors <span className="text-slate-400 text-[10px] normal-case ml-1">preview updates live</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {COLOR_FIELDS.map((f) => {
                const val = s[f.k] || "";
                const bad = val && !HEX_RE.test(val);
                return (
                  <div key={f.k} className={`border rounded-xl p-3 bg-white ${bad ? "border-rose-300" : "border-slate-200"}`}>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">{f.l}</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="color"
                        data-testid={`branding-${f.k}-picker`}
                        value={HEX_RE.test(val) ? val : "#000000"}
                        onChange={(e) => updateField(f.k, e.target.value)}
                        className="w-12 h-10 rounded-md border border-slate-200 cursor-pointer p-0 bg-white"
                        aria-label={`${f.l} color picker`}
                      />
                      <Input
                        data-testid={`branding-${f.k}-hex`}
                        value={val}
                        onChange={(e) => updateField(f.k, e.target.value)}
                        className={`font-mono text-xs uppercase ${bad ? "border-rose-300" : ""}`}
                        placeholder="#0ea5e9"
                      />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1.5">{f.help}</div>
                    {bad && <div className="text-[11px] text-rose-500 mt-1">Must be #RGB or #RRGGBB</div>}
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button data-testid="branding-save-btn" disabled={busy || invalidHex.length > 0} onClick={save}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
              <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save branding"}
            </Button>
            <a href="/" target="_blank" rel="noreferrer" data-testid="branding-open-storefront"
               className="text-xs text-slate-500 underline hover:text-slate-800">Open storefront in new tab →</a>
          </div>
        </div>

        {/* ──────────────── RIGHT: preview ──────────────── */}
        <div className="lg:col-span-1">
          <Card className="p-0 overflow-hidden sticky top-4" data-testid="branding-preview">
            <div className="px-4 py-2.5 bg-slate-100 text-slate-500 text-[11px] uppercase tracking-wider font-semibold">
              Live preview
            </div>
            {/* Header mock */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 bg-white">
              <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center"
                   style={{ background: s.accent_color || "#134e4a" }}>
                {logoSrc
                  ? <img src={logoSrc} alt="" className="max-w-full max-h-full object-contain p-1" />
                  : <span className="text-white text-[10px] font-bold">LOGO</span>}
              </div>
              <div className="font-heading font-semibold text-sm" style={{ color: s.accent_color || "#134e4a" }}>
                {s.brand_name || "Your brand"}
              </div>
            </div>
            {/* Hero mock */}
            <div className="px-5 py-6" style={{ background: `linear-gradient(135deg, ${s.accent_color || "#134e4a"}, ${s.primary_color || "#14b8a6"})` }}>
              <div className="text-white/70 text-[10px] uppercase tracking-widest">Hero</div>
              <div className="text-white text-lg font-heading font-semibold mt-1 leading-tight">Where next?</div>
              <div className="mt-3 flex gap-2">
                <button className="rounded-full px-3 h-8 text-xs text-white font-medium shadow-sm"
                        style={{ background: s.primary_color || "#14b8a6" }}>Search</button>
                <button className="rounded-full px-3 h-8 text-xs font-medium"
                        style={{ background: s.secondary_color || "#86efac", color: s.accent_color || "#134e4a" }}>Deals</button>
              </div>
            </div>
            {/* Card mock */}
            <div className="px-4 py-4 bg-white space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Sample booking card</div>
              <div className="text-sm font-semibold" style={{ color: s.accent_color || "#134e4a" }}>CAI → JED</div>
              <div className="text-[11px] text-slate-500">Egyptair · Fri 15 Jul · 2h 25m nonstop</div>
              <div className="pt-2 flex items-center justify-between">
                <div className="text-xs text-slate-500">from</div>
                <div className="text-base font-semibold" style={{ color: s.primary_color || "#14b8a6" }}>USD 133</div>
              </div>
            </div>
            {/* Footer mock */}
            <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500">
              {s.footer_powered_by || "Powered by Your Brand"} · {s.contact_email || "support@example.com"}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
